import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ExternalLink, Heart } from 'lucide-react';
import { Button } from '../components/ui/button';

export default function ResourcesPage() {
  return (
    <div>
      {/* Hero Section */}
      <section className="relative py-20 lg:py-28 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 right-20 w-72 h-72 bg-teal-500/20 rounded-full blur-3xl" />
          <div className="absolute bottom-10 left-20 w-64 h-64 bg-amber-500/20 rounded-full blur-3xl" />
        </div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl sm:text-5xl font-bold mb-6">Links</h1>
          <p className="text-xl text-slate-300">Reentry & Recovery Resources (Core National Resources)</p>
        </div>
      </section>

      {/* Housing & Transitional Support */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-slate-900 mb-8">Housing & Transitional Support (National)</h2>
          
          <div className="space-y-8">
            <div className="border-b border-slate-200 pb-6">
              <h3 className="font-bold text-lg text-slate-900 mb-2">U.S. Department of Housing and Urban Development (HUD)</h3>
              <p className="text-slate-600 mb-3">Rental assistance, public housing, and homelessness resources</p>
              <a href="https://www.hud.gov/topics/rental_assistance" target="_blank" rel="noopener noreferrer" className="inline-flex items-center text-teal-600 hover:text-teal-700 font-medium">
                https://www.hud.gov/topics/rental_assistance <ExternalLink className="w-4 h-4 ml-2" />
              </a>
            </div>
            
            <div className="border-b border-slate-200 pb-6">
              <h3 className="font-bold text-lg text-slate-900 mb-2">Benefits.gov – Housing & Basic Needs</h3>
              <p className="text-slate-600 mb-3">Federal and state benefits screening</p>
              <a href="https://www.benefits.gov" target="_blank" rel="noopener noreferrer" className="inline-flex items-center text-teal-600 hover:text-teal-700 font-medium">
                https://www.benefits.gov <ExternalLink className="w-4 h-4 ml-2" />
              </a>
            </div>
            
            <div className="border-b border-slate-200 pb-6">
              <h3 className="font-bold text-lg text-slate-900 mb-2">GoSection8</h3>
              <p className="text-slate-600 mb-3">Section 8 housing listings (availability varies by state and locality)</p>
              <a href="https://www.gosection8.com" target="_blank" rel="noopener noreferrer" className="inline-flex items-center text-teal-600 hover:text-teal-700 font-medium">
                https://www.gosection8.com <ExternalLink className="w-4 h-4 ml-2" />
              </a>
            </div>
            
            <div>
              <h3 className="font-bold text-lg text-slate-900 mb-2">U.S. Department of Veterans Affairs – Homeless Programs</h3>
              <p className="text-slate-600 mb-3">Housing and support services for veterans, including justice-involved veterans</p>
              <a href="https://www.va.gov/homeless/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center text-teal-600 hover:text-teal-700 font-medium">
                https://www.va.gov/homeless/ <ExternalLink className="w-4 h-4 ml-2" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Reentry Support (National) */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-slate-900 mb-8">Reentry Support (National)</h2>
          
          <div className="space-y-8">
            <div className="border-b border-slate-200 pb-6">
              <h3 className="font-bold text-lg text-slate-900 mb-2">National Reentry Resource Center</h3>
              <p className="text-slate-600 mb-3">Federal reentry planning tools and guidance</p>
              <a href="https://nationalreentryresourcecenter.org" target="_blank" rel="noopener noreferrer" className="inline-flex items-center text-teal-600 hover:text-teal-700 font-medium">
                https://nationalreentryresourcecenter.org <ExternalLink className="w-4 h-4 ml-2" />
              </a>
            </div>
            
            <div className="border-b border-slate-200 pb-6">
              <h3 className="font-bold text-lg text-slate-900 mb-2">Legal Aid Finder (Legal Services Corporation)</h3>
              <p className="text-slate-600 mb-3">Free or low-cost legal aid by state</p>
              <a href="https://www.lsc.gov/about-lsc/what-legal-aid/find-legal-aid" target="_blank" rel="noopener noreferrer" className="inline-flex items-center text-teal-600 hover:text-teal-700 font-medium">
                https://www.lsc.gov/about-lsc/what-legal-aid/find-legal-aid <ExternalLink className="w-4 h-4 ml-2" />
              </a>
            </div>
            
            <div className="border-b border-slate-200 pb-6">
              <h3 className="font-bold text-lg text-slate-900 mb-2">State & Local Reentry Councils</h3>
              <p className="text-slate-600 mb-3">Search via state government or Department of Corrections websites</p>
              <p className="text-slate-500 italic">(availability varies by state)</p>
            </div>
            
            <div>
              <h4 className="text-xl font-bold text-slate-900 mb-4">Youth Reentry</h4>
              <h3 className="font-bold text-lg text-slate-900 mb-2">Office of Juvenile Justice and Delinquency Prevention (OJJDP)</h3>
              <p className="text-slate-600 mb-3">Youth reentry and transition resources</p>
              <a href="https://ojjdp.ojp.gov" target="_blank" rel="noopener noreferrer" className="inline-flex items-center text-teal-600 hover:text-teal-700 font-medium">
                https://ojjdp.ojp.gov <ExternalLink className="w-4 h-4 ml-2" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Recovery & Wellness */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-slate-900 mb-8">Recovery & Wellness</h2>
          
          <h3 className="text-xl font-bold text-slate-700 mb-6">Healthcare & Community Access</h3>
          
          <div className="space-y-8">
            <div className="border-b border-slate-200 pb-6">
              <h3 className="font-bold text-lg text-slate-900 mb-2">Healthcare.gov – Find Local Help</h3>
              <p className="text-slate-600 mb-3">Health insurance options, enrollment assistance, and local navigators</p>
              <a href="https://www.healthcare.gov/find-local-help/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center text-teal-600 hover:text-teal-700 font-medium">
                https://www.healthcare.gov/find-local-help/ <ExternalLink className="w-4 h-4 ml-2" />
              </a>
            </div>
            
            <div>
              <h3 className="font-bold text-lg text-slate-900 mb-2">Public Libraries (Local)</h3>
              <p className="text-slate-600 mb-3">Access to computers, internet, job search tools, GED study materials, and community programs</p>
              <p className="text-slate-500 italic mb-3">Find your local public library via your city or county website</p>
              <p className="text-slate-500 text-sm">
                (Public libraries are included as community wellness and stability resources that support education, employment, and access to information.)
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Reentry Support (Additional Resources) */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-slate-900 mb-8">Reentry Support (Additional Resources)</h2>
          
          <h3 className="text-xl font-bold text-slate-700 mb-6">Residential Reentry & Mapping Tools</h3>
          
          <div className="space-y-8">
            <div className="border-b border-slate-200 pb-6">
              <h3 className="font-bold text-lg text-slate-900 mb-2">Federal Bureau of Prisons – Residential Reentry Centers (RRCs)</h3>
              <p className="text-slate-600 mb-3">Information on halfway houses and residential reentry centers used primarily during federal incarceration and supervision</p>
              <a href="https://www.bop.gov/about/facilities/residential_reentry_management_centers.jsp" target="_blank" rel="noopener noreferrer" className="inline-flex items-center text-teal-600 hover:text-teal-700 font-medium break-all">
                https://www.bop.gov/about/facilities/residential_reentry_management_centers.jsp <ExternalLink className="w-4 h-4 ml-2 flex-shrink-0" />
              </a>
            </div>
            
            <div className="border-b border-slate-200 pb-6">
              <h3 className="font-bold text-lg text-slate-900 mb-2">National Institute of Corrections (NIC)</h3>
              <p className="text-slate-600 mb-3">National criminal justice initiatives, reentry resources, and system-level mapping tools</p>
              <a href="https://nicic.gov" target="_blank" rel="noopener noreferrer" className="inline-flex items-center text-teal-600 hover:text-teal-700 font-medium">
                https://nicic.gov <ExternalLink className="w-4 h-4 ml-2" />
              </a>
            </div>
            
            <p className="text-slate-500 italic">
              (Availability, eligibility, and placement into RRCs vary by jurisdiction, supervision status, and whether an individual is under federal or state authority.)
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-teal-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Need Help Navigating Resources?
          </h2>
          <p className="text-xl text-teal-100 mb-10">
            Contact us for assistance finding the right resources for your situation.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button asChild size="lg" className="bg-white text-teal-700 hover:bg-teal-50">
              <Link to="/contact">
                Contact Us
                <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </Button>
            <Button asChild size="lg" className="bg-amber-500 hover:bg-amber-400 text-white">
              <Link to="/get-involved#donate">
                <Heart className="w-4 h-4 mr-2" />
                Support the Mission
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
